import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const STORAGE_KEY = 'sportyreplay_onboarded';

const STEPS = [
  { icon: '🔍', title: 'Artikel finden', desc: 'Durchsuche gebrauchte Sportartikel von Vereinen in deiner Region' },
  { icon: '💬', title: 'Verkäufer kontaktieren', desc: 'Schreib direkt eine Nachricht über den Chat-Button auf der Produktseite' },
  { icon: '🤝', title: 'Treffen & Abholen', desc: 'Vereinbart einen Treffpunkt und holt den Artikel persönlich ab' },
  { icon: '📦', title: 'Versand auf Anfrage', desc: 'Manche Verkäufer verschicken auch – einfach per Chat nachfragen' },
];

const OnboardingPopup: React.FC = () => {
  const location = useLocation();
  const [visible, setVisible] = useState<boolean>(false);

  // Onboarding nur auf der Startseite, nur einmal und erst NACH der
  // Cookie-Entscheidung zeigen -> kein Overlay-Stapel mit dem Cookie-Banner.
  useEffect(() => {
    if (location.pathname !== '/') return;

    const check = () => {
      try {
        const onboarded = localStorage.getItem(STORAGE_KEY);
        const consent = localStorage.getItem('cookieConsent');
        if (!onboarded && consent) {
          setVisible(true);
          return true;
        }
      } catch {}
      return false;
    };

    if (check()) return;
    // Auf Cookie-Entscheidung warten, dann einmalig zeigen
    const interval = setInterval(() => { if (check()) clearInterval(interval); }, 500);
    const timeout = setTimeout(() => clearInterval(interval), 30000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [location.pathname]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="onboarding-popup"
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="w-full md:max-w-lg bg-white rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg leading-tight">Willkommen bei SportyReplay! 👋</h2>
            <p className="text-white/80 text-sm mt-0.5">So einfach funktioniert's:</p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Schließen"
            className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-5 py-4 space-y-3">
          {STEPS.map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-lg">
                {step.icon}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{step.title}</p>
                <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="px-5 pb-6">
          <button
            data-testid="onboarding-cta"
            onClick={dismiss}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all text-base"
          >
            Los geht's!
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPopup;
